import { useQuery } from '@tanstack/react-query';
import { businessAccessApi } from '../api/businessAccessClient';
import { useBusinessContext } from '../context/BusinessContext';
import { useUserPermissions } from './useUserPermissions';
import { PaymentsListResponse } from '../types/payment.types';

/**
 * Custom hook to fetch payments for the current business
 * If user is admin, fetches all payments across all businesses
 * @returns React Query result with payments data
 */
export const useBusinessPayments = () => {
  const { business } = useBusinessContext();
  const { isAdmin, isLoading: isLoadingPermissions } = useUserPermissions();
  const businessId = business?.id;

  return useQuery({
    queryKey: ['business-payments', isAdmin ? 'all' : businessId],
    queryFn: async () => {
      const url = isAdmin ? '/payments' : `/payments/${businessId}`;

      if (!isAdmin && !businessId) {
        throw new Error('Business ID is required');
      }

      const response = await businessAccessApi<any>({
        url,
        method: 'GET',
      });

      // Normalize response — API may return { payments: [...] } or { data: { payments: [...] } } or just [...]
      let payments = [];
      if (Array.isArray(response)) {
        payments = response;
      } else if (response?.payments) {
        payments = response.payments;
      } else if (response?.data?.payments) {
        payments = response.data.payments;
      } else if (Array.isArray(response?.data)) {
        payments = response.data;
      }

      return { payments } as PaymentsListResponse;
    },
    // Wait for permissions to load before enabling the query
    enabled: !isLoadingPermissions && (isAdmin ? true : !!businessId),
    refetchOnWindowFocus: true,
    staleTime: 30000, // 30 seconds
  });
};

/**
 * Fetch payments for a SPECIFIC business id (same `/payments/{businessId}`
 * endpoint the /payments page uses, but scoped to the given business rather
 * than the current context business). Used by the business detail Payments tab.
 */
export const useBusinessPaymentsById = (businessId?: string | null, enabled = true) => {
  return useQuery({
    queryKey: ['business-payments', 'by-id', businessId],
    queryFn: async () => {
      const response = await businessAccessApi<any>({
        url: `/payments/${businessId}`,
        method: 'GET',
      });

      let payments = [];
      if (Array.isArray(response)) {
        payments = response;
      } else if (response?.payments) {
        payments = response.payments;
      } else if (response?.data?.payments) {
        payments = response.data.payments;
      } else if (Array.isArray(response?.data)) {
        payments = response.data;
      }

      return { payments } as PaymentsListResponse;
    },
    enabled: enabled && !!businessId,
    refetchOnWindowFocus: true,
    staleTime: 30000,
  });
};
